// src/pages/Ad/ActiveAdReport.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdPreviewDialog from '../../components/AdPreviewDialog';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const ActiveAdReport = () => {
  // State Variables
  const [ads, setAds] = useState([]);
  const [totalAds, setTotalAds] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const { can } = usePermissions();

  // Status Action Modal State (Pause / Unpause / Stop with Reason)
  const [actionModal, setActionModal] = useState({
    open: false,
    adId: null,
    adHeader: '',
    targetStatus: '', // 'paused', 'active', 'stopped', 'inActive'
    reason: '',
  });

  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [previewAd, setPreviewAd] = useState(null);

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch Live Ads Function
  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: 'active',
        live: true, // only ads running today (IST): start date <= today <= end date
        page: page + 1,
        limit: pageSize,
      };

      const response = await axios.get(`${API_BASE_URL}/ads`, { params });

      if (response.status === 200) {
        const { ads: rawAds, total } = response.data;
        const mappedAds = (rawAds || []).map((ad, index) => ({
          id: ad._id,
          slNo: index + 1 + page * pageSize,
          header: ad.header || 'N/A',
          details: ad.details || 'N/A',
          startDate: ad.startDate ? new Date(ad.startDate).toLocaleDateString('en-GB') : 'N/A',
          endDate: ad.endDate ? new Date(ad.endDate).toLocaleDateString('en-GB') : 'N/A',
          status: ad.status || 'active',
          statusReason: ad.statusReason || '',
          adType: ad.adType ? ad.adType.toUpperCase() : 'N/A',
          providerName: ad.providerId?.name || 'N/A',
          rawAd: ad,
        }));

        setAds(mappedAds);
        setTotalAds(total || mappedAds.length);
      } else {
        setAds([]);
        setTotalAds(0);
        showSnackbar('Failed to fetch live ads.', 'error');
      }
    } catch (error) {
      console.error('Error fetching live ads:', error);
      setAds([]);
      setTotalAds(0);
      showSnackbar('Error fetching live ads.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // Open Action Modal (Pause / Unpause / Stop)
  const handleOpenActionModal = (adId, adHeader, targetStatus) => {
    if (!can('ads.live.changeStatus')) return;
    setActionModal({
      open: true,
      adId,
      adHeader,
      targetStatus,
      reason: '',
    });
  };

  // Close Action Modal
  const handleCloseActionModal = () => {
    setActionModal({
      open: false,
      adId: null,
      adHeader: '',
      targetStatus: '',
      reason: '',
    });
  };

  // Submit Status Update with Reason
  const handleSubmitStatusAction = async () => {
    if (!can('ads.live.changeStatus')) return;
    if (!actionModal.reason.trim()) {
      alert('Please enter a reason before proceeding.');
      return;
    }

    setActionSubmitting(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/ads/${actionModal.adId}/status`, {
        status: actionModal.targetStatus,
        reason: actionModal.reason.trim(),
      });

      if (response.status === 200) {
        showSnackbar(`Ad has been set to ${actionModal.targetStatus} successfully.`, 'success');
        handleCloseActionModal();
        fetchAds();
      } else {
        showSnackbar('Failed to update ad status.', 'error');
      }
    } catch (error) {
      console.error('Error updating ad status:', error);
      const msg = error.response?.data?.message || 'Failed to update ad status.';
      showSnackbar(msg, 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Snackbar Helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('ads.live.export')) return;
    if (ads.length === 0) {
      showSnackbar('No data available to export.', 'error');
      return;
    }

    const headers = [
      'Sl No',
      'Header',
      'Details',
      'Start Date',
      'End Date',
      'Status',
      'Reason / Remarks',
      'Ad Type',
      'Provider Name'
    ];

    const rows = ads.map((ad) => [
      ad.slNo,
      `"${ad.header.replace(/"/g, '""')}"`,
      `"${ad.details.replace(/"/g, '""')}"`,
      ad.startDate,
      ad.endDate,
      ad.status,
      `"${(ad.statusReason || '').replace(/"/g, '""')}"`,
      ad.adType,
      `"${ad.providerName.replace(/"/g, '""')}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `live_ads_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getActionTitle = () => {
    if (actionModal.targetStatus === 'paused') return 'Pause Advertisement';
    if (actionModal.targetStatus === 'active') return 'Unpause Advertisement';
    if (actionModal.targetStatus === 'stopped' || actionModal.targetStatus === 'inActive') return 'Stop Advertisement';
    return 'Update Advertisement Status';
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BackButton />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', ml: 1 }}>
            Live Ads Report (Pause / Unpause / Stop with Reason)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAds}>
            Refresh
          </Button>
          {can('ads.live.export') && (
            <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExportCSV}>
              CSV
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>SL No</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Preview</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ad Header</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ad Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Provider Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Actions (Pause / Unpause / Stop)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#64748b' }}>
                    No live advertisements found.
                  </TableCell>
                </TableRow>
              ) : (
                ads.map((ad) => {
                  const isPaused = ad.status === 'paused';
                  const isStopped = ad.status === 'inActive' || ad.status === 'stopped';

                  return (
                    <TableRow key={ad.id} hover>
                      <TableCell>{ad.slNo}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setPreviewAd(ad.rawAd)}>
                          Preview
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{ad.header}</TableCell>
                      <TableCell sx={{ maxWidth: 200, whiteSpace: 'normal' }}>{ad.details}</TableCell>
                      <TableCell>{ad.startDate}</TableCell>
                      <TableCell>{ad.endDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={ad.status.toUpperCase()}
                          color={isPaused ? 'warning' : isStopped ? 'error' : 'success'}
                          size="small"
                        />
                        {ad.statusReason && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Reason: {ad.statusReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{ad.adType}</TableCell>
                      <TableCell>{ad.providerName}</TableCell>
                      <TableCell align="center">
                        {can('ads.live.changeStatus') ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {isPaused ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<PlayCircleOutlineIcon />}
                                onClick={() => handleOpenActionModal(ad.id, ad.header, 'active')}
                              >
                                Unpause
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<PauseCircleOutlineIcon />}
                                onClick={() => handleOpenActionModal(ad.id, ad.header, 'paused')}
                                disabled={isStopped}
                              >
                                Pause
                              </Button>
                            )}

                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<StopCircleIcon />}
                              onClick={() => handleOpenActionModal(ad.id, ad.header, 'stopped')}
                              disabled={isStopped}
                            >
                              Stop
                            </Button>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Action Modal (Pause / Unpause / Stop with Reason) */}
      <AdPreviewDialog ad={previewAd} onClose={() => setPreviewAd(null)} />

      <Dialog open={actionModal.open} onClose={handleCloseActionModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{getActionTitle()}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to update the status for <strong>"{actionModal.adHeader}"</strong> to{' '}
            <strong>{actionModal.targetStatus.toUpperCase()}</strong>.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Enter Reason / Remarks"
            placeholder="Type reason here..."
            value={actionModal.reason}
            onChange={(e) => setActionModal({ ...actionModal, reason: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseActionModal} disabled={actionSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionModal.targetStatus === 'active' ? 'success' : actionModal.targetStatus === 'paused' ? 'warning' : 'error'}
            onClick={handleSubmitStatusAction}
            disabled={actionSubmitting}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActiveAdReport;